import Fuse from "fuse.js";
import { normalize, searchSite } from "./siteSearch";

// Answer engine for the site assistant.
//
// Three tiers, tried in order:
//   1. Curated FAQ — a direct, authored answer, in the visitor's language.
//   2. Site content — no authored answer exists, but a page covers it, so the
//      page's own description is quoted and linked.
//   3. Fallback — hand off to the contact form.
//
// Tier 2 is what makes the assistant useful beyond its five FAQ entries: it
// reuses the same index and Turkish-aware matching as the Navigator, so
// "cv koçluğu" or "eleman arıyorum" now reach the right page instead of
// dead-ending. No answer text is invented — tier 2 quotes existing page copy.

// `terms` are matching aids only (including undiacriticked and colloquial
// spellings, in both languages). They are never shown to the user.
const FAQ = [
  {
    question: { tr: "merhaba, selam", en: "hello, hi" },
    answer: {
      tr: "Merhaba! Bir sorunuz varsa size yardımcı olmayı çok isterim :)",
      en: "Hello! If you have a question, I'd be glad to help :)",
    },
    terms: [
      "merhaba", "selam", "iyi günler", "günaydın", "merhabalar", "hey",
      "hello", "hi", "good morning", "good afternoon",
    ],
    suggest: false,
  },
  {
    question: { tr: "Çalışma saatleriniz nedir?", en: "What are your working hours?" },
    answer: {
      tr: "Pazartesi - Cuma, 09:00 - 18:00 arasında hizmet veriyoruz.",
      en: "We are available Monday to Friday, 09:00 to 18:00.",
    },
    terms: [
      "çalışma saatleri", "calisma saatleri", "saat", "kaçta açık", "kaça kadar",
      "ne zaman açıksınız", "mesai", "hafta sonu", "açılış", "kapanış",
      "working hours", "opening hours", "office hours", "hours", "when are you open",
      "weekend", "what time",
    ],
    suggest: true,
  },
  {
    question: { tr: "CV hazırlama desteği veriyor musunuz?", en: "Do you help with CV preparation?" },
    answer: {
      tr: "Evet, adaylar için CV danışmanlığı hizmetimiz mevcuttur.",
      en: "Yes, we offer CV consulting for candidates.",
    },
    terms: [
      "cv", "özgeçmiş", "ozgecmis", "cv hazırlama", "cv desteği", "cv danışmanlığı",
      "resume", "cv yazma", "cv help", "cv writing", "cv preparation", "cv review",
    ],
    suggest: true,
  },
  {
    question: { tr: "İşverenlere nasıl destek oluyorsunuz?", en: "How do you support employers?" },
    answer: {
      tr: "İşe alım stratejileri, mülakat süreçleri ve aday değerlendirme konularında destek veriyoruz.",
      en: "We support employers with recruitment strategy, interview processes and candidate assessment.",
    },
    terms: [
      "işveren", "isveren", "şirket", "firma", "kurum", "işveren desteği",
      "işe alım", "mülakat süreci", "aday değerlendirme",
      "employer", "employers", "company", "employer support", "recruitment",
      "interview process", "candidate assessment",
    ],
    suggest: true,
  },
  {
    question: { tr: "Hangi sektörlerle çalışıyorsunuz?", en: "Which industries do you work with?" },
    answer: {
      tr: "Farklı sektörlerdeki firmalara özel İK çözümleri sunmaktayız.",
      en: "We provide tailored HR solutions to companies across different industries.",
    },
    terms: [
      "sektör", "sektorler", "hangi sektör", "alan", "endüstri", "hangi alanlarda",
      "sector", "sectors", "industry", "industries", "which industries", "fields",
    ],
    suggest: true,
  },
];

const FALLBACK = {
  tr: "Üzgünüm, bu konuda bilgim yok. Bilgi almak için iletişim formumuzdan bize ulaşabilirsiniz.",
  en: "Sorry, I don't have information on that. You can reach us through the contact form.",
};

const CONTACT_LABEL = { tr: "İletişim", en: "Contact" };

const toLang = (locale) => (locale === "en" ? "en" : "tr");

// The questions offered as one-tap prompts, in the visitor's language.
export function getSuggestions(locale = "tr") {
  const lang = toLang(locale);
  return FAQ.filter((f) => f.suggest).map((f) => f.question[lang]);
}

const entries = FAQ.map((f) => ({
  ...f,
  _questions: [normalize(f.question.tr), normalize(f.question.en)],
  _terms: f.terms.map(normalize),
  _search: [normalize(f.question.tr), normalize(f.question.en), ...f.terms.map(normalize)].join(" "),
}));

const faqFuse = new Fuse(entries, {
  keys: ["_search"],
  includeScore: true,
  ignoreLocation: true,
  threshold: 0.4,
  minMatchCharLength: 3,
});

/**
 * Answer a question.
 * @returns {{text: string, link?: {href: string, label: string}, source: string}}
 */
export function answerQuestion(query, locale = "tr") {
  const lang = toLang(locale);
  const q = normalize(query);
  if (!q) return null;

  // 1. Curated FAQ. Term hits win outright, so short queries ("cv") and
  //    natural phrasings ("saat kaça kadar açıksınız") both land before the
  //    fuzzy pass gets a chance to mis-score them.
  const exact = entries.find((e) => e._questions.includes(q) || e._terms.includes(q));
  if (exact) return { text: exact.answer[lang], source: "faq" };

  const strong = entries.find((e) =>
    e._terms.some((t) => t.length >= 4 && q.includes(t))
  );
  if (strong) return { text: strong.answer[lang], source: "faq" };

  const hit = faqFuse.search(q);
  if (hit.length && (hit[0].score ?? 1) < 0.4) {
    return { text: hit[0].item.answer[lang], source: "faq" };
  }

  // 2. A page covers it — quote that page and offer the link.
  const { results, confidence } = searchSite(query, lang, 1);
  if (results.length && confidence >= 0.5) {
    const top = results[0];
    return {
      text: top.description,
      link: { href: top.href, label: top.title },
      source: "site",
    };
  }

  // 3. Hand off.
  return {
    text: FALLBACK[lang],
    link: { href: "/iletisim", label: CONTACT_LABEL[lang] },
    source: "fallback",
  };
}
